import { useCallback } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";

import { Icon } from "@/components/Icon";
import { BomNode } from "@/features/job-tracking/queries/tracking-data/bom-hierarchy-generator";
import { BomPathPurchasingPriority } from "@/features/job-tracking/queries/tracking-data/purchasing-priorities";
import { UpdatePurchasingPriorityMutation } from "@/features/job-tracking/queries/tracking-data/useUpdatePurchasingPriorityMutation";

export interface SavePurchasingPriorityTemplateButtonProps {
  bomNode: BomNode;
  updatePurchasingPriorityMutation: UpdatePurchasingPriorityMutation;
  className: string;
}

/**
 * Component that renders a button to save all job-specific purchasing priority records for an assembly as the new
 * template for that assembly, and then delete the job-specific records so that they are replaced by the template.
 */
export const SavePurchasingPriorityTemplateButton: React.FC<SavePurchasingPriorityTemplateButtonProps> = ({
  bomNode,
  updatePurchasingPriorityMutation,
  className,
}) => {
  // Define the button click handler
  const handleDeleteCallBack = useCallback(
    () => void handleDelete(bomNode, updatePurchasingPriorityMutation),
    [bomNode, updatePurchasingPriorityMutation],
  );

  // Render nothing unless this is a top-level node with a job-specific record
  if (bomNode.recursiveDepth !== 0 || !bomNode.purchasingPriority.nodeJobRecord) return null;

  return (
    <OverlayTrigger overlay={<Tooltip>Save As Template</Tooltip>}>
      <Button
        variant="link"
        size="sm"
        disabled={updatePurchasingPriorityMutation.isPending}
        onClick={handleDeleteCallBack}
        className={className}
      >
        <Icon icon="floppy-fill" className="text-secondary" />
      </Button>
    </OverlayTrigger>
  );
};

async function handleDelete(bomNode: BomNode, updatePurchasingPriorityMutation: UpdatePurchasingPriorityMutation) {
  // Do nothing if this node doesn't have a job-specific purchasing priority record
  if (!bomNode.purchasingPriority.nodeJobRecord) return;

  // Confirm with the user before proceeding
  const confirmResult = confirm(
    "This will save the the current priorities for this assembly and all its children as the new priority template " +
      "for this assembly.\n\nThis will replace (i.e. delete) the existing template for this assembly, and the new " +
      "template will immediately apply to any other sales orders with this assembly\n\nThis action cannot be undone, " +
      "are you sure you want to continue?",
  );
  if (!confirmResult) return;

  // Get all template purchasing priority records for this node's children
  const templateRecords = getAllTemplatePriorityRecords(bomNode.children);

  // Get all job-specific purchasing priority records for this node and its children
  const jobSpecificRecords = [
    bomNode.purchasingPriority.nodeJobRecord,
    ...getAllJobSpecificPriorityRecords(bomNode.children),
  ];

  // Apply a mutation to delete any existing template records
  await updatePurchasingPriorityMutation.mutateAsync({ action: "delete", recordsToDelete: templateRecords });

  // Apply a mutation to create new template records based on the job-specific records
  await updatePurchasingPriorityMutation.mutateAsync({
    action: "upsert",
    upsertDetails: jobSpecificRecords.map((jobRecord) => ({
      jobSpecific: false,
      path: jobRecord.path,
      priority: jobRecord.priority,
      recordToUpdate: null, // Any template records were deleted in the previous mutation so we can assume null here
    })),
  });

  // Apply a mutation to delete the job-specific records
  await updatePurchasingPriorityMutation.mutateAsync({
    action: "delete",
    recordsToDelete: jobSpecificRecords,
  });
}

function getAllJobSpecificPriorityRecords(bomNodes: BomNode[]): BomPathPurchasingPriority[] {
  return bomNodes
    .filter((node) => node.purchasingPriority.nodeJobRecord)
    .map((node) => node.purchasingPriority.nodeJobRecord!)
    .concat(bomNodes.flatMap((node) => getAllJobSpecificPriorityRecords(node.children)));
}

function getAllTemplatePriorityRecords(bomNodes: BomNode[]): BomPathPurchasingPriority[] {
  return bomNodes
    .filter((node) => node.purchasingPriority.nodeTemplateRecord)
    .map((node) => node.purchasingPriority.nodeTemplateRecord!)
    .concat(bomNodes.flatMap((node) => getAllTemplatePriorityRecords(node.children)));
}
